import {
  forwardRef,
  type AriaAttributes,
  type ReactElement,
  type ComponentPropsWithoutRef,
} from "react";
import cx from "classnames";
import styles from "./FieldMessage.module.scss";
import { StatusIcon } from "../../internal/components/StatusIcon";

/**
 * Props for the FieldMessage component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type FieldMessageProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * Unique identifier for the field message container.
   */
  id?: string;
  /**
   * Hint text or element to display. Always visible regardless of error or warning state.
   */
  hint?: ReactElement | string;
  /**
   * Description text or element to display below the hint.
   */
  description?: ReactElement | string;
  /**
   * Error message(s) to display. Supports a single string, an array of strings,
   * or a ReactElement (deprecated).
   * All error messages use `aria-live="assertive"`.
   */
  error?: string | string[] | ReactElement;
  /**
   * Warning message(s) to display. Supports a single string, an array of strings,
   * or a ReactElement (deprecated).
   * All warning messages use `aria-live="polite"`.
   */
  warning?: string | string[] | ReactElement;
  /**
   * Error message or element to display when there's an error.
   * @deprecated Use the `error` prop instead.
   */
  errorMessage?: ReactElement | string;
  /**
   * @deprecated No longer used. Error messages always use `aria-live="assertive"`.
   */
  errorAriaLive?: AriaAttributes["aria-live"];
};

/** Normalizes a string | string[] | ReactElement into a renderable array. */
function normalizeMessages(
  val: string | string[] | ReactElement | undefined,
): (string | ReactElement)[] {
  if (val === undefined) return [];
  if (Array.isArray(val)) return val;
  return [val];
}

/**
 * FieldMessage component for displaying hints, descriptions, error messages, and warning messages for form fields.
 *
 * Features:
 * - Displays hint text that remains visible regardless of error or warning state
 * - Shows one or more error messages with error icon (aria-live="assertive")
 * - Shows one or more warning messages with warning icon (aria-live="polite")
 * - Supports both string and React element content
 * - Optional description text for additional context
 * - Accessible with proper ARIA attributes
 * - Visual error and warning indicators with status icons
 *
 * @example
 * <FieldMessage
 *   hint="Enter your email address"
 *   description="We'll use this to send you updates"
 *   error="Please enter a valid email address"
 *   warning="This email is associated with another account"
 * />
 */
export const FieldMessage = forwardRef<HTMLDivElement, FieldMessageProps>(
  (props, ref) => {
    const {
      id,
      hint,
      description,
      error,
      warning,
      errorAriaLive: _errorAriaLive,
      errorMessage,
      className,
      ...rest
    } = props;

    const errorMessages = normalizeMessages(error);
    const warningMessages = normalizeMessages(warning);

    // Backward compatibility: if deprecated errorMessage is provided and new error prop is not
    const allErrors =
      errorMessages.length === 0 && errorMessage
        ? [errorMessage]
        : errorMessages;

    return (
      <div
        {...rest}
        className={cx(styles["messages"], className)}
        ref={ref}
        id={id}
      >
        {hint ?? null}
        {description ? (
          <div className={styles["description-container"]}>
            <span>{description}</span>
          </div>
        ) : null}
        {allErrors.map((msg, index) => (
          <div key={index} className={styles["error-container"]}>
            <StatusIcon
              variant="error"
              rootProps={{
                "aria-hidden": true,
                className: styles["status-icon"],
                style: { color: "inherit" },
              }}
            />
            <span className={styles["error-message"]} aria-live="assertive">
              {msg}
            </span>
          </div>
        ))}
        {warningMessages.map((msg, index) => (
          <div key={index} className={styles["warning-container"]}>
            <StatusIcon
              variant="warning"
              rootProps={{
                "aria-hidden": true,
                className: styles["status-icon"],
                style: { color: "inherit" },
              }}
            />
            <span className={styles["warning-message"]} aria-live="polite">
              {msg}
            </span>
          </div>
        ))}
      </div>
    );
  },
);

FieldMessage.displayName = "FieldMessage";
